import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DashboardSection } from './DashboardSection';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { borders } from '../../design-system/tokens/borders';
import { AdminActionItem } from '../../types/dashboard';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const Panel = styled(DashboardSection)``;

const PanelTitle = styled(Heading)`
  margin: 0 0 ${spacing[2]} 0 !important;
`;

const PanelSubtitle = styled(Text)`
  display: block;
  margin-bottom: ${spacing[5]};
`;

const ActionsGrid = styled.div<{ $secondary?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[3]};

  @media (min-width: ${breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${breakpoints.lg}) {
    grid-template-columns: ${props => (props.$secondary ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))')};
  }
`;

const ActionButton = styled.button<{ $secondary?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[2]};
  text-align: left;
  padding: ${spacing[4]} ${spacing[5]};
  border: 1px solid
    ${props =>
      props.$secondary ? props.theme.colors.border : props.theme.colors.primary};
  border-radius: ${borders.radius.md};
  background: ${props =>
    props.$secondary
      ? 'transparent'
      : `${props.theme.colors.primary}0F`};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ActionLabel = styled.span<{ $secondary?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  font-weight: ${props =>
    props.$secondary
      ? typography.fontWeight.medium
      : typography.fontWeight.semibold};
  color: ${props =>
    props.$secondary ? props.theme.colors.text : props.theme.colors.primary};
`;

const ActionLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${spacing[2]};
`;

const ActionDesc = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${typography.lineHeight.relaxed};
`;

const Arrow = styled(ArrowRightIcon)`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: ${props => props.theme.colors.textSecondary};
`;

interface AdminActionGroupsProps {
  primary: AdminActionItem[];
  secondary: AdminActionItem[];
}

export const AdminActionGroups: React.FC<AdminActionGroupsProps> = ({
  primary,
  secondary,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <Panel>
        <PanelTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
          {t('admin.home.actions.primaryTitle')}
        </PanelTitle>
        <PanelSubtitle size="sm" color="secondary">
          {t('admin.home.actions.primarySubtitle')}
        </PanelSubtitle>
        <ActionsGrid>
          {primary.map(action => (
            <ActionButton key={action.id} type="button" onClick={() => navigate(action.link)}>
              <ActionLabelRow>
                <ActionLabel>{action.label}</ActionLabel>
                <Arrow aria-hidden />
              </ActionLabelRow>
              <ActionDesc>{action.description}</ActionDesc>
            </ActionButton>
          ))}
        </ActionsGrid>
      </Panel>

      <Panel>
        <PanelTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
          {t('admin.home.actions.secondaryTitle')}
        </PanelTitle>
        <PanelSubtitle size="sm" color="secondary">
          {t('admin.home.actions.secondarySubtitle')}
        </PanelSubtitle>
        <ActionsGrid $secondary>
          {secondary.map(action => (
            <ActionButton
              key={action.id}
              type="button"
              $secondary
              onClick={() => navigate(action.link)}
            >
              <ActionLabelRow>
                <ActionLabel $secondary>{action.label}</ActionLabel>
                <Arrow aria-hidden />
              </ActionLabelRow>
              <ActionDesc>{action.description}</ActionDesc>
            </ActionButton>
          ))}
        </ActionsGrid>
      </Panel>
    </>
  );
};

export default AdminActionGroups;
